import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { toZonedTime } from "date-fns-tz";
import { Phone, UserRound } from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import type { SubmitHandler } from "react-hook-form";
import { useSearchParams } from "react-router-dom";

import { useCheckPatientStatus, useLeadSources } from "@/hooks";
import { useMultiStepFormStore } from "@/stores";
import { newIntakeFormSchema } from "@/types";
import type { NewIntakeFormInputType } from "@/types";
import {
  Button,
  Combobox,
  FormField,
  Input,
  NotQualifyModal,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  Spinner,
} from "@/ui/common";
import { MONTHS } from "@/utils";

const EST_TIMEZONE = "America/New_York";

export default function GeneralInformationForm() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const { setMultiStepFormData, multiStepFormData } = useMultiStepFormStore();
  const {
    register,
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors },
  } = useForm<NewIntakeFormInputType>({
    resolver: zodResolver(newIntakeFormSchema),
  });
  const { mutate: checkPatient, isPending } = useCheckPatientStatus();
  const { data: leadSources, isLoading: isLeadSourcesLoading } =
    useLeadSources();

  const onSubmit: SubmitHandler<NewIntakeFormInputType> = (data) => {
    const { day, month, year, ...rest } = data;
    setMultiStepFormData({
      generalInformationFormData: {
        dateOfBirth: { month, day, year },
        ...rest,
      },
    });
    const birthdate = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

    checkPatient({
      firstName: rest.firstName,
      lastName: rest.lastName,
      birthdate,
    });
  };

  useEffect(() => {
    const listId = searchParams.get("list_id");
    const vendorId = searchParams.get("vendor_id");
    const firstName = searchParams.get("first_name");
    const lastName = searchParams.get("last_name");
    const dateOfBirth = searchParams.get("date_of_birth");
    const email = searchParams.get("email");
    const phoneNumber = searchParams.get("phone_number");

    let day;
    let month;
    let year;

    if (dateOfBirth?.includes("/")) {
      const [d, m, y] = dateOfBirth.split("/");
      day = Number(d);
      month = Number(m);
      year = Number(y);
    }

    const mappedLeadSource = leadSources?.find(
      (leadSource) => leadSource.id === Number(listId),
    )?.label;

    reset({
      listId: listId ?? "",
      firstName: firstName ?? "",
      lastName: lastName ?? "",
      day,
      month,
      year,
      email: email ?? "",
      phoneNumber: phoneNumber ?? "",
      leadSourceName: mappedLeadSource ?? "",
      vendorId: vendorId ?? "",
    });
  }, [searchParams, reset, leadSources]);

  useEffect(() => {
    if (multiStepFormData?.generalInformationFormData) {
      const { generalInformationFormData } = multiStepFormData;
      const { day, month, year } = generalInformationFormData.dateOfBirth;
      reset({ day, month, year, ...generalInformationFormData });
    }
  }, [multiStepFormData, reset]);

  useEffect(() => {
    const isWithinWorkingHours = () => {
      const nowLocal = new Date();
      const nowEST = toZonedTime(nowLocal, EST_TIMEZONE);

      const hour = nowEST.getHours();
      return hour >= 9 && hour < 18;
    };

    if (!isWithinWorkingHours()) {
      setIsModalOpen(true);
    }
  }, []);

  if (isPending) {
    return <Spinner />;
  }

  return (
    <div className="flex flex-col gap-y-10">
      <div className="flex justify-between">
        <div className="flex items-center gap-2">
          <UserRound className="text-primary-600" size={16} />
          <p className="font-extrabold">General information</p>
        </div>
        <p className="text-sm font-extrabold text-neutral-500">
          All fields are required
        </p>
      </div>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="grid grid-cols-2 gap-x-12 gap-y-10"
      >
        <FormField label=" Lead source" error={errors.leadSourceName?.message}>
          <Controller
            control={control}
            name="leadSourceName"
            render={({ field, fieldState }) => (
              <Combobox
                isLoading={isLeadSourcesLoading}
                options={
                  leadSources?.map((leadSource) => leadSource.label) ?? []
                }
                value={field.value}
                onChange={(selectedLeadSource) => {
                  field.onChange(selectedLeadSource);
                  const listId = leadSources?.find(
                    (leadSource) => leadSource.label === selectedLeadSource,
                  )?.id;

                  setValue("listId", String(listId));
                }}
                hasError={!!fieldState.error?.message}
                placeholder="Select or search lead source"
              />
            )}
          />
        </FormField>
        <FormField label="List ID" error={errors.listId?.message}>
          <Input
            placeholder="XXXXXX"
            {...register("listId")}
            error={errors.listId?.message}
            disabled={true}
          />
        </FormField>
        <FormField label="First name" error={errors.firstName?.message}>
          <Input
            placeholder="E.g. John"
            error={errors.firstName?.message}
            {...register("firstName")}
          />
        </FormField>
        <FormField label="Last name" error={errors.lastName?.message}>
          <Input
            placeholder="E.g. Doe"
            error={errors.lastName?.message}
            {...register("lastName")}
          />
        </FormField>

        <FormField label="Date of birth" error={errors.month?.message}>
          <div className="grid grid-cols-3 gap-2">
            <Controller
              control={control}
              name="month"
              render={({ field, fieldState }) => (
                <SelectGroup>
                  <Select
                    value={field.value ? String(field.value) : undefined}
                    onValueChange={(value) => field.onChange(Number(value))}
                  >
                    <SelectTrigger
                      placeholder="Month"
                      className={
                        fieldState.error
                          ? "border-error-500"
                          : "border-neutral-200"
                      }
                    />
                    <SelectContent>
                      {MONTHS.map(({ id, value, label }) => (
                        <SelectItem key={id} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </SelectGroup>
              )}
            />
            <Input
              id="day"
              placeholder="Day"
              type="number"
              error={errors.day?.message}
              onInput={(e: React.FormEvent<HTMLInputElement>) => {
                const input = e.currentTarget;
                if (input.value.length > 2) {
                  input.value = input.value.slice(0, 2);
                }
              }}
              {...register("day", {
                setValueAs: (v: string) => {
                  if (Number.isInteger(v)) return v;
                  return v.trim() === "" ? undefined : parseInt(v, 10);
                },
              })}
            />

            <Input
              id="year"
              placeholder="Year"
              type="number"
              error={errors.year?.message}
              onInput={(e: React.FormEvent<HTMLInputElement>) => {
                const input = e.currentTarget;
                if (input.value.length > 2) {
                  input.value = input.value.slice(0, 4);
                }
              }}
              {...register("year", {
                setValueAs: (v: string) => {
                  if (Number.isInteger(v)) return v;
                  return v.trim() === "" ? undefined : parseInt(v, 10);
                },
              })}
            />
          </div>
        </FormField>
        <FormField label="Email" error={errors.email?.message}>
          <Input
            type="email"
            placeholder="E.g. johndoe@gmail.com"
            {...register("email")}
            error={errors.email?.message}
          />
        </FormField>
        <FormField label="Phone number" error={errors.phoneNumber?.message}>
          <Input
            type="tel"
            placeholder="0000000000"
            {...register("phoneNumber")}
            error={errors.phoneNumber?.message}
            icon={<Phone size={16} />}
          />
        </FormField>

        <div className="col-span-2 flex justify-end">
          <Button variant="primary" type="submit" className={"w-3/12"}>
            Next
          </Button>
        </div>
      </form>
      <NotQualifyModal
        show={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={"Episode alert unavailable"}
        description={
          "Episode Alert is out of working hours. Please try again between 9:00 AM and 8:00 PM."
        }
      />
    </div>
  );
}
